import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    Box,
    Button,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem
} from '@mui/material';
import { useAuth } from '../context/authContext';
import React, { useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu'; // Import menu icon for mobile
import { Link, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { NetworkStatusIndicator } from './networkStatus';
import { useUserRole } from '../context/userRoleContext';
import { ArchiveOutlined, Diversity3 } from '@mui/icons-material';
import { APP_VERSION } from '../version';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, userName, logout, login } = useAuth();
    const { userRole, isAdmin } = useUserRole();
    const appVersion = APP_VERSION;
    const normalizedRole = userRole?.toLowerCase();
    const roleLabel = normalizedRole === 'user'
        ? "Chef d'équipe"
        : normalizedRole === 'foreman'
            ? "Conducteur de travaux"
            : normalizedRole === 'hr'
                ? "Ressources humaines"
                : normalizedRole === 'admin'
                    ? "Administrateur"
                    : "Rôle non défini";

    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleLogin = () => {
        login();
    };

    const handleLogout = () => {
        logout();
    };

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        setDrawerOpen(open);
    };

    // Navigation items for both desktop and mobile views
    const navigationItems = React.useMemo(() => {
        const items = [
            // Only show Dashboard for Admin users
            ...(isAdmin ? [{ text: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard' }] : []),
            // Only show New Report for all users
            ...((normalizedRole === "user" || normalizedRole === "foreman" || isAdmin) ?
                [{ text: 'Nouveau rapport', icon: <AddTaskIcon />, path: '/timesheet' }] : []),
            { text: 'Tous les rapports', icon: <ListAltIcon />, path: '/timesheetList' },
            // Only show Team Management for WorkManagers users
            ...((normalizedRole === "user" || normalizedRole === "foreman" || isAdmin) ? [{ text: 'gestion d\'équipes', icon: <Diversity3 />, path: '/teams' }] : []),
            ...((normalizedRole === "hr" || isAdmin)
                ? [{ text: 'Export', icon: <ArchiveOutlined />, path: '/export' }]
                : []),
        ];
        return items;
    }, [normalizedRole, isAdmin]);

    const drawerContent = (
        <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <img
                    src="/logo.jpg"
                    alt="Company Logo"
                    style={{
                        height: '24px',
                        width: 'auto',
                        marginRight: '8px'
                    }}
                />
                <Typography variant="h6">Timesheet App</Typography>
            </Box>
            <Divider />
            <List>
                {navigationItems.map((item) => (
                    <ListItem
                        button
                        key={item.text}
                        component={Link}
                        to={item.path}
                        selected={location.pathname === item.path}
                        sx={{
                            backgroundColor: location.pathname === item.path ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                    </ListItem>
                ))}
            </List>
            <Divider />
            {isAuthenticated && (
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {userName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {roleLabel}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleLogout}
                        sx={{ mt: 2, width: '100%' }}
                    >
                        Se déconnecter
                    </Button>
                </Box>
            )}
            <Divider />
            <Typography
                variant="caption"
                sx={{
                    cursor: 'help',
                    opacity: 0.7,
                    '&:hover': { opacity: 1 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.text.primary
                }}
            >
                v{appVersion}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 1, paddingTop: { xs: '56px', sm: '64px' } }}>
            <AppBar position="fixed">
                <Toolbar>
                    <Box
                        component={Link}
                        to="/"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            color: 'inherit',
                            mr: 2
                        }}
                    >
                        <img
                            src="/logo.jpg"
                            alt="Company Logo"
                            style={{
                                height: '42px',
                                width: 'auto',
                                marginRight: '8px'
                            }}
                        />
                    </Box>

                    {isAuthenticated && !isMobile && (
                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
                            {navigationItems.map((item) => (
                                <Button
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    startIcon={item.icon}
                                    color="inherit"
                                    sx={{
                                        textTransform: 'none',
                                        borderBottom: location.pathname === item.path ? '2px solid white' : 'none',
                                        borderRadius: 0,
                                    }}
                                >
                                    {item.text}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {isAuthenticated && isMobile && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={toggleDrawer(true)}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    <Box sx={{ flexGrow: isMobile ? 1 : 0 }} />

                    <NetworkStatusIndicator />

                    {isAuthenticated ? (
                        <Box sx={{ ml: 2 }}>
                            <IconButton
                                color="inherit"
                                onClick={(event) => setAnchorEl(event.currentTarget)}
                                size="small"
                                sx={{
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    borderRadius: '8px',
                                    padding: '4px 8px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <PersonIcon sx={{ mr: 1 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        display: { xs: 'none', sm: 'block' },
                                        textTransform: 'none'
                                    }}
                                >
                                    {userName}
                                </Typography>
                            </IconButton>

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                                PaperProps={{
                                    elevation: 3,
                                    sx: {
                                        minWidth: '200px',
                                        borderRadius: '8px',
                                        mt: 1
                                    }
                                }}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {userName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {roleLabel}
                                    </Typography>
                                </Box>
                                <Divider />
                                <MenuItem
                                    onClick={handleLogout}
                                    sx={{
                                        py: 1.5,
                                        color: 'error.main',
                                        '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                    }}
                                >
                                    Se déconnecter
                                </MenuItem>
                                <Divider />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        cursor: 'help',
                                        opacity: 0.7,
                                        '&:hover': { opacity: 1 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: theme.palette.text.primary
                                    }}
                                >
                                    v{appVersion}
                                </Typography>
                            </Menu>
                        </Box>
                    ) : (
                        <Button color="inherit" onClick={handleLogin}>
                            Se connecter
                        </Button>
                    )}
                </Toolbar>
            </AppBar>

            {/* Mobile drawer navigation */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
            >
                {drawerContent}
            </Drawer>

            <Container sx={{ mt: 4, pb: 4 }} style={{ padding: 0 }}>
                {children}
            </Container>
        </Box>
    );
};
